import { DateTime } from 'luxon'

import { isValidPrice } from '@domain-group/javascript-utils'

import { useForm } from '@domain-group/react-hooks'

import useProviderPriceList from '@hooks/useProviderPriceList'

import type { BulkUpdateProviderPriceListsParams } from '@hooks/useProviderPriceList'

import type { ProviderPriceListModel } from '@models/types'

import type { ModalCallbacks } from '../index'

type DefaultState = {
  direction: string,
  expiresAt: string,
  percentage: number,
  roundToNearestDollar: boolean,
}

const requiredFields = [
  { key: 'direction' },
  { key: 'percentage', validation: isValidPrice },
]

const defaultState = {
  direction: '',
  expiresAt: '',
  percentage: 0,
  roundToNearestDollar: true,
}

const calculatePercentage = (state: DefaultState) => {
  const { direction, percentage, roundToNearestDollar } = state
  const percentageInt = Number(percentage)

  switch (direction){
    case 'increase':
      return {
        percentage: 100 + percentageInt,
        undoChange: false,
        roundToNearestDollar,
      }
    case 'decrease':
      return {
        percentage: 100 - percentageInt,
        undoChange: false,
        roundToNearestDollar,
      }
    case 'undoIncrease':
      return {
        percentage: 100 + percentageInt,
        undoChange: true,
        roundToNearestDollar: false,
      }
    case 'undoDecrease':
      return {
        percentage: 100 - percentageInt,
        undoChange: true,
        roundToNearestDollar: false,
      }
    default:
      return {
        percentage: 100,
        undoChange: false,
        roundToNearestDollar: false,
      }
  }
}

type HandleSubmitParams = {
  callbacks: {
    bulkUpdateFn: BulkUpdateProviderPriceListsParams,
    bulkUpdateProviderPriceLists: ModalCallbacks['bulkUpdateProviderPriceLists'],
    closeModal: ModalCallbacks['closeModal'],
    deselectAll: () => void,
  },
  expiresAt: string,
  priceListIds: number[],
  entityState: DefaultState,
}

const afterAction = (callbacks: HandleSubmitParams['callbacks']) => {
  const { closeModal, deselectAll } = callbacks

  closeModal()
  deselectAll()
}

const handleSubmit = (params: HandleSubmitParams) => {
  const {
    callbacks,
    callbacks: {
      bulkUpdateFn,
      bulkUpdateProviderPriceLists,
    },
    priceListIds,
    entityState,
    entityState: {
      expiresAt,
    },
  } = params

  const { percentage, undoChange, roundToNearestDollar } = calculatePercentage(entityState)

  const bulkUpdatePayload = {
    callbacks: {
      action: bulkUpdateFn,
      afterAction: () => afterAction(callbacks),
    },
    entityParams: {
      expires_at: expiresAt,
      percentage,
      price_list_ids: priceListIds,
      undo_change: undoChange,
      round_to_nearest_dollar: roundToNearestDollar,
    },
    requestOptions: {
      include_provider_price_list_item_option_values: true,
      include_provider_price_list_item_options: true,
      include_provider_price_list_items: true,
    },
    toastText: 'Price Lists Updated',
  }

  // @ts-expect-error TS2345
  bulkUpdateProviderPriceLists(bulkUpdatePayload)
}

interface BulkUpdateProviderPriceListsModalCallbacks extends ModalCallbacks {
  deselectAll: () => void,
}

const useBulkUpdateProviderPriceListsModal = (
  callbacks: BulkUpdateProviderPriceListsModalCallbacks,
  providerPriceList: Partial<ProviderPriceListModel>,
) => {
  const {
    callbacks: { bulkUpdateProviderPriceLists: bulkUpdateFn },
    updating,
  } = useProviderPriceList(providerPriceList)

  const {
    entityState,
    handlers,
    saveEnabled,
    setEntityState,
  } = useForm(defaultState, { requiredFields, validateOn: 'change' }, [])

  const currentDateTime = DateTime.now().toFormat('yyyy-MM-dd')

  return {
    callbacks: {
      handleSubmit: (priceListIds: number[]) => handleSubmit({
        // @ts-expect-error TS2322
        callbacks: { bulkUpdateFn, ...callbacks }, priceListIds, entityState,
      }),
    },
    currentDateTime,
    entityState,
    handlers,
    setEntityState,
    saveEnabled,
    updating,
  }
}

export default useBulkUpdateProviderPriceListsModal
